import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { motion } from 'framer-motion';
import { 
  Bold, 
  Italic, 
  List, 
  ListOrdered, 
  Lightbulb, 
  CheckCircle,
  AlertCircle 
} from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  error?: string;
  className?: string;
  showOptimizationTips?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Describe your ideal target audience...',
  maxLength = 1000,
  error,
  className = '',
  showOptimizationTips = true,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const text = editor.getText();
      if (text.length <= maxLength) {
        onChange(editor.getHTML());
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-neutral max-w-none min-h-[120px] px-4 py-3 focus:outline-none',
      },
    },
  });

  useEffect(() => {
    if (editor && editor.getHTML() !== value) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  const currentLength = editor?.getText().length || 0;
  const isNearLimit = currentLength > maxLength * 0.8;
  const isOverLimit = currentLength > maxLength;

  // Optimization tips based on content length and quality
  const getOptimizationTips = () => {
    const text = editor?.getText() || '';
    const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;
    const tips = [];

    if (wordCount < 10) {
      tips.push({
        type: 'warning',
        message: 'Add more details about your target audience for better AI processing',
      });
    }

    if (!text.toLowerCase().includes('company') && !text.toLowerCase().includes('business')) {
      tips.push({
        type: 'suggestion',
        message: 'Consider mentioning company size or type for more targeted results',
      });
    }

    if (!text.toLowerCase().includes('role') && !text.toLowerCase().includes('title')) {
      tips.push({
        type: 'suggestion',
        message: 'Include job titles or roles to improve targeting accuracy',
      });
    }

    if (wordCount > 15 && wordCount < 30) {
      tips.push({
        type: 'success',
        message: 'Great detail level! This should produce excellent targeting results',
      });
    }

    return tips;
  };

  const optimizationTips = getOptimizationTips();

  if (!editor) {
    return null;
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Editor toolbar */}
      <div className="flex items-center gap-2 p-2 bg-neutral-50 border border-neutral-200 rounded-lg">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={`
            p-2 rounded-md transition-colors
            ${editor.isActive('bold') 
              ? 'bg-primary-100 text-primary-700' 
              : 'hover:bg-neutral-200 text-neutral-600'
            }
          `}
          type="button"
        >
          <Bold className="w-4 h-4" />
        </button>
        
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={`
            p-2 rounded-md transition-colors
            ${editor.isActive('italic') 
              ? 'bg-primary-100 text-primary-700' 
              : 'hover:bg-neutral-200 text-neutral-600'
            }
          `}
          type="button"
        >
          <Italic className="w-4 h-4" />
        </button>

        <div className="w-px h-6 bg-neutral-300" />

        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`
            p-2 rounded-md transition-colors
            ${editor.isActive('bulletList') 
              ? 'bg-primary-100 text-primary-700' 
              : 'hover:bg-neutral-200 text-neutral-600'
            }
          `}
          type="button"
        >
          <List className="w-4 h-4" />
        </button>

        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`
            p-2 rounded-md transition-colors
            ${editor.isActive('orderedList') 
              ? 'bg-primary-100 text-primary-700' 
              : 'hover:bg-neutral-200 text-neutral-600'
            }
          `}
          type="button"
        >
          <ListOrdered className="w-4 h-4" />
        </button>

        <div className="flex-1" />

        {/* Character count */}
        <div className={`
          text-sm font-medium px-3 py-1 rounded-md
          ${isOverLimit 
            ? 'text-error-600 bg-error-50' 
            : isNearLimit 
              ? 'text-warning-600 bg-warning-50' 
              : 'text-neutral-500 bg-neutral-100'
          }
        `}>
          {currentLength}/{maxLength}
        </div>
      </div>

      {/* Editor content */}
      <div className={`
        border rounded-xl transition-all duration-200 ease-out
        ${error ? 'border-error-500 bg-error-50' : 'border-neutral-300 bg-white hover:border-neutral-400'}
        ${editor.isFocused ? 'border-primary-500 shadow-glow-primary' : ''}
      `}>
        <EditorContent editor={editor} />
      </div>

      {/* Error message */}
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-error-600 flex items-center gap-2"
        >
          <AlertCircle className="w-4 h-4" />
          {error}
        </motion.p>
      )}

      {/* Optimization tips */}
      {showOptimizationTips && optimizationTips.length > 0 && !error && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-2"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-neutral-700">
            <Lightbulb className="w-4 h-4 text-warning-500" />
            Optimization Tips
          </div>
          
          <div className="space-y-2">
            {optimizationTips.map((tip, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`
                  flex items-start gap-3 p-3 rounded-lg text-sm
                  ${tip.type === 'success' 
                    ? 'bg-success-50 text-success-700 border border-success-200' 
                    : tip.type === 'warning'
                      ? 'bg-warning-50 text-warning-700 border border-warning-200'
                      : 'bg-primary-50 text-primary-700 border border-primary-200'
                  }
                `}
              >
                {tip.type === 'success' ? (
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                )}
                <span>{tip.message}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}; 